import { useCallback } from 'react';
import { useBrokerIsExternal } from '@/common/hooks/useBrokerIsExternal';
import { useReportsConfiguration } from './useReportsConfiguration';

export function useReportsRole() {
  const { data: config, isLoading, error } = useReportsConfiguration();
  const role = config?.highestRole ?? null;

  return {
    role,
    isAgent: !role,
    isPartner:
      role === 'PARTNER_AGENT' || role === 'PARTNER_AGENT_WITH_SUBTEAM',
    isTeamLead: role === 'TEAM_LEAD' || role === 'TEAM_LEAD_WITH_SUBTEAM',
    isLoading,
    error,
  };
}

export type ReportsDataGroup = 'agent' | 'team' | 'brand' | 'side';

export function useRoleCanSee() {
  const { role, isLoading, error } = useReportsRole();
  const { isExternal: brokerIsExternal } = useBrokerIsExternal();

  const roleCanSee = useCallback(
    (dataGroup: ReportsDataGroup) => {
      if (isLoading || error) {
        return false;
      }

      switch (dataGroup) {
        case 'brand': {
          // team leads and partners with subteams can see brand data
          return (
            role === 'TEAM_LEAD_WITH_SUBTEAM' ||
            role === 'PARTNER_AGENT_WITH_SUBTEAM' ||
            role === 'PARTNER_AGENT'
          );
        }
        case 'team': {
          return (
            role === 'TEAM_LEAD' ||
            role === 'TEAM_LEAD_WITH_SUBTEAM' ||
            role === 'PARTNER_AGENT_WITH_SUBTEAM'
          );
        }
        case 'side':
          // only partners at Side-owned (non-BYOB) brokerages can see side data
          return (
            !brokerIsExternal &&
            (role === 'PARTNER_AGENT' || role === 'PARTNER_AGENT_WITH_SUBTEAM')
          );
        default:
          // all roles can see agent data
          return true;
      }
    },
    [role, isLoading, error, brokerIsExternal],
  );

  return { roleCanSee } as const;
}
